<template>
    <div class="sidebar-container">
        <div class="sidebar-content">
            <template v-if="!$screens.mobile">
                <location-search
                    :region-model="regionModel"
                />
                <div
                    class="map-container flex-shrink flex-grow"
                >
                    <dynamic-resize-container>
                        <template #default="{width}">
                            <lga-map
                                v-if="mapModel"
                                :width="width"
                                :height="width"
                                class="flex-grow flex-shrink"
                                :model="mapModel"
                            />
                        </template>
                    </dynamic-resize-container>
                </div>
            </template>
            <div class="categories">
                <div
                    v-for="page in pagesModel.pages"
                    :key="page.id"
                    :class="{
                        category: true,
                        selected: pagesModel.hasSelected(page)
                    }"
                >
                    <a
                        :href="`#${page.domId}`"
                        onclick="javascript:void(0)"
                        @click="categoryClicked"
                    >
                        <div class="category-content">
                            <svg-inline :src="page.icon" />
                            <span class="category-label">{{ page.menu || page.title }}</span>
                        </div>
                        <div
                            v-if="pagesModel.isSelected(page) && !$screens.mobile"
                            class="highlight-bar"
                        >|</div>
                    </a>
                    <div v-if="pagesModel.hasSelected(page)">
                        <a
                            v-for="subPage in titledSubPages(page)"
                            :key="subPage.id"
                            :href="`#${subPage.domId}`"
                            onclick="javascript:void(0)"
                            :class="{
                                section: true,
                                selected: pagesModel.isSelected(subPage) && !linksModel.link
                            }"
                            @click="linksModel.link = null"
                        >
                            {{ subPage.title }}
                            <div
                                v-if="pagesModel.isSelected(subPage) && !linksModel.link && !$screens.mobile"
                                class="highlight-bar"
                            >|</div>
                        </a>
                    </div>
                </div>
            </div>

            <div class="links">
                <a
                    v-for="{id, label, icon} in linksModel.links"
                    :key="id"
                    onclick="javascript:void(0)"
                    :class="{
                        selected: linksModel.link === id
                    }"
                    @click="linkClicked(id)"
                >
                    <div class="category-content">
                        <svg-inline
                            :src="icon"
                        />
                        <span class="label">{{ label }}</span>
                    </div>
                </a>

                <Controls
                    v-if="$screens.mobile"
                    :model="controlsModel"
                />
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import MapModel from '@/model/MapModel';
import RegionModel from '@/model/RegionModel';
import PagesModel from '@/model/PagesModel';
import LinksModel from '@/model/LinksModel';
import ControlsModel from '@/model/ControlsModel';

import DynamicResizeContainer from '@/components/DynamicResizeContainer.vue';
import LgaMap from '@/components/LgaMap.vue';
import SvgInline from '@/components/SvgInline.vue';
import LocationSearch from './LocationSearch.vue';
import Controls from './Controls.vue';

export default {
    components: {
        DynamicResizeContainer,
        LgaMap,
        LocationSearch,
        SvgInline,
        Controls
    },
    props: {
        regionModel: {
            type: RegionModel,
            required: true
        },
        pagesModel: {
            type: PagesModel,
            required: true
        },
        mapModel: {
            type: MapModel,
            required: true
        },
        linksModel: {
            type: LinksModel,
            required: true
        },
        controlsModel: {
            type: ControlsModel,
            required: false
        },
    },
    methods: {
        searchForRegion(value) {
            if (value.length < 3) {
                return [];
            }
            return this.regionModel.searchForRegion(value);
        },
        titledSubPages(page) {
            return _.filter(page.pages, 'title');
        },
        categoryClicked() {
            this.linksModel.link = null;
            if (this.controlsModel) this.controlsModel.showPDF = false;
            document.body.style.overflowY = 'scroll';
        },
        linkClicked(id) {
            this.linksModel.link = id;
            if (this.controlsModel) this.controlsModel.showPDF = false;
        }
    }
};
</script>
<style scoped lang="postcss">
.sidebar-container {
    @apply flex-shrink-0
        relative sticky
        justify-start bg-neutral-5;
}

.sidebar-content {
    @apply flex flex-col
        justify-between;
}

.search-container {
    @apply border-b border-neutral-5 flex flex-col justify-center flex-grow-0;
    height: 75px;
    min-height: 50px;
}

.label {
    @apply
        uppercase
        text-neutral-3
        text-xs
        font-bold;
}

/* TODO: Make this more robust and flexible */
 @media screen and (max-height: 1075px) {
    .sidebar-container a.section {
        /* display: none !important; */
    }
}

.map-container {
    @apply flex-shrink-0 flex-grow-0;
}

hr {
    @apply
        mx-4
}

.categories {
    @apply flex flex-col;
}

div.category > a {
    @apply font-bold;
}

div.category {
    @apply my-1;
    line-height: 1;
}

div.category:first-of-type {
    margin-top: 0;
}

div.category:last-of-type {
    margin-bottom: 0;
}

div.category a {
    @apply
        flex
        flex-row
        justify-between
        items-center
        w-full
        text-left
        pb-0
        text-neutral-3
        cursor-pointer
        relative;
}

div.category a.section {
    @apply
        flex
        flex-row
        justify-between
        items-center
        w-full
        text-left
        text-xs
        pl-8
        py-1
        leading-tight
        text-neutral-3;
}

a.section {
    @apply mb-0;
}

div.category a.section.selected {
    @apply text-primary-4 font-bold;
}

div.category:not(.selected) a.section {
    @apply hidden;
}

.category-content {
    @apply flex flex-row items-center;
}

.category-content > span {
    @apply uppercase ml-2 pr-1;
}

div.category a i {
    @apply pr-2;
    font-size: 32pt !important;
}

div.category.selected a {
    @apply text-primary-4;
}

div.highlight-bar {
    @apply bg-primary-4 text-primary-4 select-none w-1 h-full rounded-full absolute;
    right: -4px;
    max-height: 26px;
    top: calc((100% - 26px) / 2);
}

.links .label {
    @apply text-neutral-3 text-xs font-normal normal-case whitespace-no-wrap;
}

.links {
    @apply flex flex-col flex-grow my-4 pt-2;
    @apply border-t border-neutral-4;
}

a {
    @apply cursor-pointer no-underline;
}

a.selected {
    @apply text-primary-4 font-bold;
}

.prototype-disclaimer {
    @apply absolute bottom-0 p-1 uppercase text-neutral-label font-bold w-full text-center;
}

.search-container {
    @apply px-8;
}

/* @screen sm { */
    .sidebar-container { width: 180pt; @apply top-header-sm h-content-height-sm; }
    .sidebar-content { @apply h-content-height-sm; }
    .category { @apply text-xs; }
    div.category a.section { @apply hidden; }
    .map-container { @apply mr-4 ml-4 mb-4; }
    .search-container { @apply px-2; }
    .links, .categories { margin-left: 22pt; margin-right: 22pt; }
/* } */
@screen md {
    .sidebar-container { @apply top-header h-content-height; }
    .sidebar-content { @apply h-content-height; }
    @media (min-height: 950px) {
        .sidebar-container { width: 210pt; }
        .category { @apply text-sm; }
        .map-container { @apply mr-6 ml-6 mb-6; }
        .links, .categories { margin-left: 30pt; margin-right: 30pt; }
        .search-container { @apply px-4; }
    }
    @media (min-height: 980px) {
        div.category a.section { @apply flex py-0; }
    }
}
@screen lg {
    .sidebar-container { @apply top-header h-content-height; }
    .sidebar-content { @apply h-content-height; }
    @media (min-height: 1050px) {
        .sidebar-container { @apply w-64;}
        .map-container { @apply mr-10 ml-10 mb-8; }
        .links, .categories { @apply mx-12; }
        div.category a.section { @apply flex py-1; }
        .search-container { @apply px-8; }
    }
}

@screen mobile {
    .sidebar-container {
        @apply top-header w-screen pt-0;
        background-color: #ffffff;
        min-height: calc(100vh - 90px);
        top: 90px;
    }
    .sidebar-content {
        @apply pt-2;
        height: unset;
        min-height: calc(100vh - 90px);
    }
    .links .category-content .label{ @apply ml-0;}
    .links, .categories { @apply mx-4; }
    .links {
        /* @apply my-4 pt-4; */
        /* @apply my-0 py-0; */
        /* position: fixed; */
        /* bottom: 0; */
    }
}

@media all and (-ms-high-contrast:none), (-ms-high-contrast: active) {
    .sidebar-container {
        height: auto !important;
        top: 0;
    }
}
</style>
<style lang="postcss">
.category-content .svg-inline {
    @apply mx-0 -ml-2 inline-flex items-center w-8 h-8;
}

.search-container .autocomplete-content {
    max-height: 250px;
}

@screen mobile {
    /* These are unscoped to apply same styles to the Controls component as well */
    .links a span.label {
        @apply
            text-neutral-3 text-sm !important;
    }
    .links a {
        /* @apply pb-2; */
    }
    .mobile-nav .dh-modal-scroll-container {
        /* overflow-y: unset !important; */
        /* min-height: 800px; */
    }
    .controls .svg-inline {
        @apply h-8 !important;
        /* width: unset !important; */
    }

    .controls .svg-inline {
        @apply h-8 !important;
        /* width: unset !important; */
    }

    .controls .svg-inline-container {
        width: unset;
    }

    .controls .download-icon .svg-inline {
        @apply w-8 !important;
        @apply -ml-2;
        /* width: unset !important; */
    }

    .controls .share-icon  {
        @apply mr-1;
    }
    @screen short {
        .controls .download-icon .svg-inline {
            @apply w-5 h-5 !important;
        }
        .controls .share-icon .svg-inline  {
            @apply h-4 !important;
            @apply -ml-2;
        }
    }

}
@screen short {
    .category-content .svg-inline {
        @apply w-5 h-5;
    }
    .sidebar-content {
        overflow-y: auto;
    }
}
</style>
